import React, { useEffect, useState } from 'react';

import {
  AddRounded, CancelRounded, CheckRounded, DeleteRounded, ViewListRounded,
} from '@mui/icons-material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { BaseColorKeys, NotifierColorKeys } from '../../theme';
import useError from '../../utils/react/hooks/useError';
import useSuccess from '../../utils/react/hooks/useSuccess';
import { ButtonProps } from '../Button';
import DataGrid, { DataGridProps } from '../DataGrid';
import DraggableList from '../DraggableList';

import {
  ActionButton, ContentGroupsListContainer, ControlBar, GrowContainer,
} from './index.styles';

type Params = {
  [key: string]: any
}

type Action = {
  fn: (params?: Params) => void,
  label?: string | ((params?: Params) => string),
  isLoading?: boolean,
  isSuccess?: boolean,
  error?: any,
  color?: BaseColorKeys | NotifierColorKeys
  | ((params?: Params) => BaseColorKeys | NotifierColorKeys),
  disabled?: boolean,
  size?: ButtonProps['size']
}

type Actions = {
  create?: Action,
  reorder?: Action,
  delete?: Action
}

interface ContentGroupsListProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  columns: GridColDef[],
  data: any[],
  loading: boolean,
  actions?: Actions,
  selectedRowId?: string,
  onRowClick?: (params: GridRowParams<any>) => void,
  checkboxSelection?: boolean,
}

const ContentGroupsList = ({ columns,
  data,
  loading,
  onRowClick = () => {},
  actions,
  selectedRowId,
  checkboxSelection = false,
  ...props }: ContentGroupsListProps) => {
  const [orderedData, setOrderedData] = useState<any>(data);
  const [reorder, setReorder] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<any>([]);

  /**
   * Success Handling
   */

  useSuccess(actions?.delete?.isSuccess, 'Entry removed');

  useEffect(() => {
    if (actions?.reorder?.isSuccess) setReorder(false);
  }, [actions?.reorder?.isSuccess]);

  useError(!!actions?.delete?.error, actions?.delete?.error);

  return (
    <ContentGroupsListContainer>
      <ControlBar>
        {reorder && (
          <>
            <ActionButton
              size={actions?.reorder?.size || 'medium'}
              color="error"
              variant="text"
              startIcon={<CancelRounded />}
              onClick={() => setReorder(false)}
            >
              Cancel
            </ActionButton>
            <ActionButton
              size={actions?.reorder?.size || 'medium'}
              startIcon={<CheckRounded />}
              loading={actions?.reorder?.isLoading}
              color="success"
              disabled={actions?.reorder?.disabled}
              onClick={async () => actions?.reorder?.fn({
                orderedData,
              })}
            >
              Commit
            </ActionButton>
          </>
        )}
        {!reorder && (
          <>
            {actions?.delete && selectedRows.length > 0 && (
              <ActionButton
                color={typeof actions?.delete?.color === 'function'
                  ? actions?.delete?.color()
                  : actions?.delete?.color || 'darkPrimary'}
                size={actions?.delete?.size || 'medium'}
                startIcon={<DeleteRounded />}
                disabled={actions?.delete?.disabled}
                onClick={async () => actions?.delete?.fn(
                  { data, selectedRows },
                )}
              >
                {typeof actions?.delete?.label === 'function'
                  ? actions?.delete?.label()
                  : actions?.delete?.label || 'Delete'}
              </ActionButton>
            )}
            {actions?.reorder && data.length > 0 && (
              <ActionButton
                color={typeof actions?.reorder?.color === 'function'
                  ? actions?.reorder?.color()
                  : actions?.reorder?.color || 'darkPrimary'}
                size={actions?.reorder.size || 'medium'}
                startIcon={<ViewListRounded />}
                disabled={actions?.reorder?.disabled}
                onClick={() => setReorder(true)}
              >
                {typeof actions?.reorder?.label === 'function'
                  ? actions?.reorder?.label()
                  : actions?.reorder?.label || 'Reorder'}
              </ActionButton>
            )}
            {actions?.create && (
              <ActionButton
                color={typeof actions?.create?.color === 'function'
                  ? actions?.create?.color()
                  : actions?.create?.color || 'darkPrimary'}
                size={actions?.create.size || 'medium'}
                startIcon={<AddRounded />}
                disabled={actions?.create?.disabled}
                onClick={async () => actions?.create?.fn()}
              >
                {(actions?.create?.label && (typeof actions?.create?.label === 'function'
                  ? actions?.create?.label()
                  : actions?.create?.label)) || 'Add'}
              </ActionButton>
            )}
          </>
        )}
      </ControlBar>
      <GrowContainer>
        {reorder ? (
          <DraggableList
            data={data.map((entry) => ({
              id: entry.id,
              label: entry?.title || entry?.label,
              value: entry,
            }))}
            onOrderChange={(x) => setOrderedData(x.map((entry) => entry.value))}
          />
        ) : (
          <DataGrid
            rows={data}
            columns={columns}
            pageSizeOptions={[10, 30, 50, 100]}
            checkboxSelection={checkboxSelection}
            getRowId={(row) => row.id}
            autoHeight
            onRowClick={onRowClick}
            loading={loading && !!data}
            selectedRowId={selectedRowId}
            onRowSelectionModelChange={(newSelectionModel) =>
              setSelectedRows(newSelectionModel)}
            rowSelectionModel={selectedRows}
            {...props}
          />
        )}
      </GrowContainer>
    </ContentGroupsListContainer>
  );
};

export default ContentGroupsList;
